import React, { useState, useEffect } from 'react';
import { Download, Search, FileText, AlertCircle, Loader } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const KsefInvoices = () => {
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Parametry wyszukiwania
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pageOffset] = useState(0);
  const [pageSize] = useState(100);
  const [subjectType, setSubjectType] = useState('Subject2'); // Subject2 = faktury zakupowe (domyślnie)
  
  // Wyniki
  const [invoices, setInvoices] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  
  // Eksport
  const [exportReferenceNumber, setExportReferenceNumber] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  
  // Ustaw domyślne daty (ostatnie 30 dni)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setDateTo(today.toISOString().split('T')[0] + 'T23:59:59');
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0] + 'T00:00:00');
  }, []);
  
  const handleSearchMetadata = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Wybierz datę od i datę do');
      return;
    }
    
    setSearching(true);
    try {
      const response = await api.post('/ksef/invoices/metadata', {
        dateFrom,
        dateTo,
        pageOffset,
        pageSize,
        sortOrder: 'Asc',
        subjectType: subjectType // Subject1 = sprzedażowe, Subject2 = zakupowe
      });
      
      if (response.data.success) {
        setInvoices(response.data.invoices || []);
        setHasMore(response.data.hasMore || false);
        setIsTruncated(response.data.isTruncated || false);
        
        if (response.data.invoices && response.data.invoices.length > 0) {
          toast.success(`Znaleziono ${response.data.invoices.length} faktur`);
        } else {
          toast.info('Nie znaleziono faktur w podanym zakresie dat');
        }
      } else {
        toast.error(response.data.message || 'Błąd podczas wyszukiwania faktur');
      }
    } catch (error) {
      console.error('Error searching invoices:', error);
      const errorMessage = error.response?.data?.message || 'Błąd podczas wyszukiwania faktur z KSeF';
      toast.error(errorMessage);
    } finally {
      setSearching(false);
    }
  };
  
  const handleStartExport = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Wybierz datę od i datę do');
      return;
    }
    
    setExporting(true);
    try {
      const response = await api.post('/ksef/invoices/exports', {
        dateFrom,
        dateTo
      });
      
      if (response.data.success) {
        setExportReferenceNumber(response.data.referenceNumber);
        
        // Wygeneruj i zapisz klucz symetryczny i IV (w rzeczywistości powinny być wygenerowane po stronie serwera)
        // Na razie używamy placeholder - w produkcji klucz powinien być generowany po stronie serwera i zapisany w sesji/cache
        toast.success('Eksport rozpoczęty. Sprawdzam status...');
        
        // Sprawdź status eksportu
        setTimeout(() => {
          checkExportStatus(response.data.referenceNumber);
        }, 2000);
      } else {
        toast.error(response.data.message || 'Błąd podczas startowania eksportu');
      }
    } catch (error) {
      console.error('Error starting export:', error);
      const errorMessage = error.response?.data?.message || 'Błąd podczas startowania eksportu faktur';
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };
  
  const checkExportStatus = async (referenceNumber) => {
    try {
      const response = await api.get(`/ksef/invoices/exports/${referenceNumber}`);
      
      if (response.data.success) {
        setExportStatus(response.data.exportStatus);
        
        const status = response.data.exportStatus?.status;
        if (status === 'Completed' || status === 'COMPLETED') {
          toast.success('Eksport zakończony. Możesz pobrać paczkę faktur.');
        } else if (status === 'Processing' || status === 'PROCESSING') {
          toast.info('Eksport w trakcie...');
          // Sprawdź ponownie za 3 sekundy
          setTimeout(() => {
            checkExportStatus(referenceNumber);
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error checking export status:', error);
      toast.error('Błąd podczas sprawdzania statusu eksportu');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Format DD.MM.YYYY jak w KSeF
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (e) {
      return dateString;
    }
  };
  
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-xl font-bold text-gray-900">Faktury z KSeF</h2>
        <p className="text-sm text-gray-600 mt-1">
          Pobieranie i przeglądanie faktur z Krajowego Systemu e-Faktur
        </p>
      </div>
      
      <div className="card-body">
        {/* Formularz wyszukiwania */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Wyszukiwanie metadanych faktur
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data od
              </label>
              <input
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data do
              </label>
              <input
                type="datetime-local"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Typ faktur
              </label>
              <select
                value={subjectType}
                onChange={(e) => setSubjectType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="Subject2">Faktury zakupowe (Podmiot2)</option>
                <option value="Subject1">Faktury sprzedażowe (Podmiot1)</option>
              </select>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleSearchMetadata}
              disabled={searching}
              className="btn btn-primary flex items-center"
            >
              {searching ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Wyszukiwanie...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Wyszukaj faktury
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Lista faktur */}
        {invoices.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">
              Znalezione faktury ({invoices.length})
            </h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Identyfikator sprzedawcy
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nazwa sprzedawcy
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nr KSeF
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nr faktury
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data wystawienia
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data zapisania w KSeF
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data otrzymania
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Netto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brutto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      VAT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tryb wysyłki faktury
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {invoice.sellerNip || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {invoice.sellerName || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">
                        {invoice.ksefNumber || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {invoice.invoiceNumber || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {formatDate(invoice.issueDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {formatDate(invoice.permanentStorageDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {formatDate(invoice.invoicingDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {invoice.netAmount != null ? `${invoice.netAmount.toFixed(2)} ${invoice.currency || 'PLN'}` : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {invoice.grossAmount != null ? `${invoice.grossAmount.toFixed(2)} ${invoice.currency || 'PLN'}` : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {invoice.vatAmount != null ? `${invoice.vatAmount.toFixed(2)} ${invoice.currency || 'PLN'}` : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {invoice.invoicingMode || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {hasMore && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  {isTruncated ? 'Osiągnięto limit wyników. Zawęź zakres dat.' : 'Więcej wyników dostępnych.'}
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Eksport paczki faktur */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Download className="h-5 w-5 mr-2" />
            Eksport paczki faktur (XML)
          </h3>
          
          <p className="text-sm text-gray-600 mb-4">
            Eksport pozwala pobrać pełne pliki XML faktur w zaszyfrowanej paczce.
            Paczka jest dzielona na części i wymaga odszyfrowania.
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={handleStartExport}
              disabled={exporting || !dateFrom || !dateTo}
              className="btn btn-primary flex items-center"
            >
              {exporting ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Eksportowanie...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Rozpocznij eksport
                </>
              )}
            </button>
            
            {exportReferenceNumber && (
              <button
                onClick={() => checkExportStatus(exportReferenceNumber)}
                className="btn btn-secondary flex items-center"
              >
                <FileText className="h-4 w-4 mr-2" />
                Sprawdź status
              </button>
            )}
          </div>
          
          {exportStatus && (
            <div className="mt-4 p-3 bg-white rounded border">
              <p className="text-sm font-medium mb-2">Status eksportu:</p>
              <p className="text-sm text-gray-600">
                {exportStatus.status || 'Nieznany'}
              </p>
              {exportStatus.package && (
                <div className="mt-2">
                  <p className="text-sm font-medium">Części paczki: {exportStatus.package.parts?.length || 0}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Informacja o wymaganiach */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 mb-1">
                Wymagane uprawnienia
              </p>
              <p className="text-sm text-yellow-700">
                Certyfikat KSeF musi mieć uprawnienie <strong>InvoiceRead</strong>.
                Sprawdź konfigurację certyfikatu w ustawieniach KSeF.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KsefInvoices;
